import React, { useState, useEffect, useCallback, useMemo, useContext } from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import Modal from "../../../ui/Modal";
import ModalTableInput from "../../../ui/ModalTableInput";
import Loader from "../../../ui/Loader";
import { AuthContext } from "../../../../context/AuthContext";
import { fetchWithAuth } from "../../../../utils/api";
import {
  swalError,
  swalSuccess,
  swalWarning,
} from "../../../../utils/swalAlert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileExcel,
  faSave,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";
import PhoneInput from "react-phone-input-2";
import { userCountry } from "../../../../utils/getUserCountry";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

// Empty student row template
const createEmptyRow = () => ({
  firstName: "",
  lastName: "",
  referenceId: "",
  rollNo: "",
  email: "",
  phoneNumber: "",
  locked: false,
  receiveWhatsAppMessages: false,
  receiveEmailMessages: false,
  errors: {},
});

const AddStudentsModal = ({
  isOpen,
  onClose,
  onStudentsCreated,
  instituteCategory,
  levelOptions = [],
  batchYearOptions = [],
  autoGenerateStudentPassword = false,
  isLanguageInst = false,
}) => {
  const { user } = useContext(AuthContext);
  const [rows, setRows] = useState([createEmptyRow()]);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [commonPassword, setCommonPassword] = useState("");
  const [showCommonPassword, setShowCommonPassword] = useState(false);
  const [currentProgress, setCurrentProgress] = useState({
    current: 0,
    total: 0,
    name: "",
  });
  const [isParsing, setIsParsing] = useState(false);

  const [instructors, setInstructors] = useState([]);
  const [selectedInstructorId, setSelectedInstructorId] = useState(null);

  const isLanguageInstitute =
    isLanguageInst ||
    instituteCategory === "LANGUAGE_LEARNING_INSTITUTE" ||
    user?.category === "LANGUAGE_LEARNING_INSTITUTE" ||
    user?.institute?.category === "LANGUAGE_LEARNING_INSTITUTE";

  // Fetch instructors if language institute (for assigning level tests)
  useEffect(() => {
    const fetchInstructors = async () => {
      const instId = user?.instituteId || user?.institute?._id;
      if (!instId) return;
      try {
        const res = await fetchWithAuth(
          `${serverUrl}/api/user/get-all/${instId}/instructor`,
          { credentials: "include" },
        );
        if (res && res.ok) {
          const data = await res.json();
          setInstructors(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error("Failed to fetch instructors in AddStudentsModal:", err);
      }
    };
    if (isLanguageInstitute) {
      fetchInstructors();
    }
  }, [user?.instituteId, user?.institute?._id, isLanguageInstitute]);

  const instructorOptions = useMemo(() => {
    return instructors.map((inst) => ({
      value: inst._id,
      label: `${inst.firstName} ${inst.lastName} (${inst.email})`,
    }));
  }, [instructors]);

  const modalLevelOptions = useMemo(() => {
    const base = Array.isArray(levelOptions) ? [...levelOptions] : [];
    if (isLanguageInstitute) {
      return [
        ...base,
        { value: "LEVEL_TEST_REQUIRED", label: "Level Test Required" },
      ];
    }
    return base;
  }, [levelOptions, isLanguageInstitute]);

  // Table headers configuration
  const headers = useMemo(
    () => [
      {
        key: "firstName",
        label: "First Name",
        width: "0.7fr",
        required: true,
      },
      {
        key: "lastName",
        label: "Last Name",
        width: "0.7fr",
        required: true,
      },
      {
        key: "referenceId",
        label: "Reference ID",
        width: "0.7fr",
        required: true,
      },
      {
        key: "rollNo",
        label: "Roll No",
        width: "0.6fr",
        required: false,
      },
      {
        key: "email",
        label: "Email",
        width: "1.7fr",
        required: false,
      },
      {
        key: "phoneNumber",
        label: "Phone",
        width: "1.3fr",
        required: false,
      },
      {
        key: "locked",
        label: "Lock",
        width: "70px",
        required: false,
        title: "Block student account",
      },
      {
        key: "receiveWhatsAppMessages",
        label: "WhatsApp Msg",
        width: "80px",
        required: false,
        title: "Enable WhatsApp notifications",
      },
      {
        key: "receiveEmailMessages",
        label: "Email Msg",
        width: "80px",
        required: false,
        title: "Enable Email notifications",
      },
      { key: "actions", label: "", width: "50px", required: false },
    ],
    [],
  );

  // Handle row data change
  const handleRowChange = useCallback((rowIndex, field, value) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[rowIndex] = {
        ...updated[rowIndex],
        [field]: value,
        errors: { ...updated[rowIndex].errors, [field]: null },
      };

      // Disable WhatsApp toggle if no phone
      if (field === "phoneNumber" && !value) {
        updated[rowIndex].receiveWhatsAppMessages = false;
      }
      // Disable email toggle if no email
      if (field === "email" && !value) {
        updated[rowIndex].receiveEmailMessages = false;
      }

      return updated;
    });
  }, []);

  // Handle row removal
  const handleRowRemove = useCallback((rowIndex) => {
    setRows((prev) => {
      if (prev.length <= 1) {
        swalWarning("At least one row is required.");
        return prev;
      }
      return prev.filter((_, idx) => idx !== rowIndex);
    });
  }, []);

  // Add new row
  const handleRowAdd = useCallback(() => {
    setRows((prev) => [...prev, createEmptyRow()]);
  }, []);

  // Helper to check if a row has any user-entered data
  const isRowNotEmpty = useCallback(
    (row) =>
      !!(
        row.firstName?.trim() ||
        row.lastName?.trim() ||
        row.referenceId?.trim() ||
        row.rollNo?.trim() ||
        row.email?.trim() ||
        row.phoneNumber
      ),
    [],
  );

  // Count of rows that have user-entered data
  const filledRowsCount = useMemo(
    () => rows.filter(isRowNotEmpty).length,
    [rows, isRowNotEmpty],
  );

  // True if at least one row has data entered
  const hasAnyData = filledRowsCount > 0;

  // Validate rows (accepting target rows)
  const validateRows = useCallback((targetRows) => {
    let isValid = true;
    const referenceIds = new Set();
    const emails = new Set();

    const validatedRows = targetRows.map((row) => {
      const errors = {};

      // Required field validation
      if (!row.firstName.trim()) {
        errors.firstName = "Required";
        isValid = false;
      }
      if (!row.lastName.trim()) {
        errors.lastName = "Required";
        isValid = false;
      }
      if (!row.referenceId.trim()) {
        errors.referenceId = "Required";
        isValid = false;
      }

      // Email format validation
      if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
        errors.email = "Invalid email";
        isValid = false;
      }

      // Uniqueness validation within the table
      if (row.referenceId.trim()) {
        const refIdLower = row.referenceId.trim().toLowerCase();
        // Only check duplicate if no prior error (e.g. Required)
        if (!errors.referenceId) {
          if (referenceIds.has(refIdLower)) {
            errors.referenceId = "Duplicate";
            isValid = false;
          }
          referenceIds.add(refIdLower);
        }
      }

      if (row.email.trim()) {
        const emailLower = row.email.trim().toLowerCase();
        // Only check duplicate if no prior error (e.g. Invalid email)
        if (!errors.email) {
          if (emails.has(emailLower)) {
            errors.email = "Duplicate";
            isValid = false;
          }
          emails.add(emailLower);
        }
      }

      return { ...row, errors };
    });

    setRows(validatedRows);
    return { isValid, validatedRows };
  }, []);

  // Clean phone number for API
  const cleanPhone = (val) =>
    val ? String(val).replace(/[+\-\s()]/g, "") : "";

  // Handle bulk creation
  const handleSave = async () => {
    // Validate level/batch selection based on institute category
    if (instituteCategory !== "HIGHER_EDUCATION" && !selectedLevel) {
      swalWarning("Please select a Level before saving students.");
      // Scroll to the dropdown selector
      setTimeout(() => {
        const dropdown = document.querySelector(".add-students-header");
        if (dropdown) {
          dropdown.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
      return;
    }
    if (selectedLevel === "LEVEL_TEST_REQUIRED" && !selectedInstructorId) {
      swalWarning("Please assign a Teacher to conduct the Level Test.");
      return;
    }
    if (instituteCategory === "HIGHER_EDUCATION" && !selectedBatch) {
      swalWarning("Please select a Batch before saving students.");
      // Scroll to the dropdown selector
      setTimeout(() => {
        const dropdown = document.querySelector(".add-students-header");
        if (dropdown) {
          dropdown.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
      return;
    }

    // Validate common password if entered
    if (commonPassword && commonPassword.length < 8) {
      swalWarning("Password must be at least 8 characters.");
      return;
    }

    // Filter out completely blank rows before validating and saving
    const rowsToSave = rows.filter(isRowNotEmpty);
    if (rowsToSave.length === 0) return;

    const { isValid, validatedRows } = validateRows(rowsToSave);
    if (!isValid) {
      // Scroll to first error after a small delay for state to update
      setTimeout(() => {
        const firstError = document.querySelector(
          ".table-validation-error, .has-error",
        );
        if (firstError) {
          firstError.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
      return;
    }

    setIsCreating(true);
    setCurrentProgress({ current: 0, total: validatedRows.length, name: "" });

    // If Level Test Required and an instructor is selected, resolve their placement-zone class
    let targetPlacementClassId = null;
    if (selectedLevel === "LEVEL_TEST_REQUIRED" && selectedInstructorId) {
      try {
        const clsRes = await fetchWithAuth(
          `${serverUrl}/api/class/get-all/${selectedInstructorId}`,
          { credentials: "include" },
        );
        if (clsRes && clsRes.ok) {
          const classes = await clsRes.json();
          if (Array.isArray(classes)) {
            const pCls = classes.find((c) => c.isLevelPlacementZone);
            if (pCls) targetPlacementClassId = pCls._id;
          }
        }
        if (!targetPlacementClassId) {
          const createClsRes = await fetchWithAuth(
            `${serverUrl}/api/class/add`,
            {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                className: "Level Test Zone",
                instructorId: selectedInstructorId,
                isLevelPlacementZone: true,
              }),
            },
          );
          if (createClsRes && createClsRes.ok) {
            const newCls = await createClsRes.json();
            targetPlacementClassId = newCls._id;
          }
        }
      } catch (clsErr) {
        console.error("Failed to resolve instructor placement class:", clsErr);
      }
    }

    const successRecords = []; // Store full row data for successful students
    const errorRecords = []; // Store row data + error message for failed students

    for (let i = 0; i < validatedRows.length; i++) {
      const row = validatedRows[i];
      const fullName = `${row.firstName} ${row.lastName}`;
      setCurrentProgress({
        current: i + 1,
        total: validatedRows.length,
        name: fullName,
      });

      try {
        const response = await fetchWithAuth(`${serverUrl}/api/user/add`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            firstName: row.firstName.trim(),
            lastName: row.lastName.trim(),
            referenceId: row.referenceId.trim(),
            rollNo: row.rollNo?.trim() || null,
            email: row.email.trim() || null,
            phoneNumber: cleanPhone(row.phoneNumber),
            receiveWhatsAppMessages: row.receiveWhatsAppMessages,
            receiveEmailMessages: row.receiveEmailMessages,
            locked: row.locked,
            role: ["student"],
            instituteId: user?.instituteId,
            batch:
              instituteCategory === "HIGHER_EDUCATION" ? selectedBatch : null,
            level:
              selectedLevel === "LEVEL_TEST_REQUIRED"
                ? ""
                : instituteCategory !== "HIGHER_EDUCATION"
                ? selectedLevel
                : null,
            isActive: selectedLevel === "LEVEL_TEST_REQUIRED" ? false : true,
            ...(selectedLevel === "LEVEL_TEST_REQUIRED"
              ? { inActiveReason: "Level Test Required" }
              : {}),
            ...(commonPassword && commonPassword.length >= 8 ? { password: commonPassword } : {}),
          }),
        });

        if (!response) continue;

        if (response.ok) {
          successRecords.push({
            firstName: row.firstName,
            lastName: row.lastName,
            referenceId: row.referenceId,
            rollNo: row.rollNo || "-",
            email: row.email || "-",
          });

          // Enroll student into the assigned teacher's Level Test Zone
          if (targetPlacementClassId) {
            try {
              await fetchWithAuth(`${serverUrl}/api/enrollment/add-student`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  classId: targetPlacementClassId,
                  email: row.email.trim() || undefined,
                  referenceId: row.referenceId.trim() || undefined,
                  firstName: row.firstName.trim(),
                  lastName: row.lastName.trim(),
                  phoneNumber: cleanPhone(row.phoneNumber),
                }),
              });
            } catch (enrollErr) {
              console.error(
                `Failed to enroll student into placement zone for ${fullName}:`,
                enrollErr,
              );
            }
          }
        } else {
          const errorData = await response.json();
          errorRecords.push({
            firstName: row.firstName,
            lastName: row.lastName,
            referenceId: row.referenceId,
            rollNo: row.rollNo || "-",
            email: row.email || "-",
            error: errorData.message || "Failed",
          });
        }
      } catch (err) {
        errorRecords.push({
          firstName: row.firstName,
          lastName: row.lastName,
          referenceId: row.referenceId,
          rollNo: row.rollNo || "-",
          email: row.email || "-",
          error: err.message,
        });
      }

      // Small delay for UI updates
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    setIsCreating(false);
    setCurrentProgress({ current: 0, total: 0, name: "" });

    // Show detailed table summary popup
    if (successRecords.length > 0 || errorRecords.length > 0) {
      // Build table rows for successful students
      const successRowsHtml = successRecords
        .map(
          (s) =>
            `<tr style="background: #d4edda;">
          <td style="padding: 8px; border: 1px solid #c3e6cb; color: #155724;">✓</td>
          <td style="padding: 8px; border: 1px solid #c3e6cb;">${s.firstName}</td>
          <td style="padding: 8px; border: 1px solid #c3e6cb;">${s.lastName}</td>
          <td style="padding: 8px; border: 1px solid #c3e6cb;">${s.referenceId || "-"}</td>
          <td style="padding: 8px; border: 1px solid #c3e6cb;">${s.rollNo || "-"}</td>
          <td style="padding: 8px; border: 1px solid #c3e6cb;">${s.email}</td>
          <td style="padding: 8px; border: 1px solid #c3e6cb; color: #155724; font-weight: 600;">Success</td>
        </tr>`,
        )
        .join("");

      // Build table rows for failed students
      const errorRowsHtml = errorRecords
        .map(
          (s) =>
            `<tr style="background: #f8d7da;">
          <td style="padding: 8px; border: 1px solid #f5c6cb; color: #721c24;">✗</td>
          <td style="padding: 8px; border: 1px solid #f5c6cb;">${s.firstName}</td>
          <td style="padding: 8px; border: 1px solid #f5c6cb;">${s.lastName}</td>
          <td style="padding: 8px; border: 1px solid #f5c6cb;">${s.referenceId || "-"}</td>
          <td style="padding: 8px; border: 1px solid #f5c6cb;">${s.rollNo || "-"}</td>
          <td style="padding: 8px; border: 1px solid #f5c6cb;">${s.email}</td>
          <td style="padding: 8px; border: 1px solid #f5c6cb; color: #721c24;">${s.error}</td>
        </tr>`,
        )
        .join("");

      const tableHtml = `
        <div style="max-height: 400px; overflow: auto; margin-top: 10px; -webkit-overflow-scrolling: touch;">
          <div style="min-width: 600px;">
            <table style="width: 100%; border-collapse: collapse; font-size: 0.85em; white-space: nowrap;">
              <thead>
                <tr style="background: #f8f9fa; position: sticky; top: 0;">
                  <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: center; width: 40px;"></th>
                  <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: left;">First Name</th>
                  <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: left;">Last Name</th>
                  <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: left;">Reference ID</th>
                  <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: left;">Roll No</th>
                  <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: left;">Email</th>
                  <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: left;">Status</th>
                </tr>
              </thead>
              <tbody>
                ${successRowsHtml}
                ${errorRowsHtml}
              </tbody>
            </table>
          </div>
        </div>
        <p style="margin-top: 8px; font-size: 0.75em; color: #888; text-align: center;">Swipe horizontally to see all columns</p>
        <div style="margin-top: 12px; display: flex; gap: 20px; justify-content: center; flex-wrap: wrap;">
          <span style="color: #155724; font-weight: 600;">✓ Success: ${successRecords.length}</span>
          <span style="color: #721c24; font-weight: 600;">✗ Failed: ${errorRecords.length}</span>
        </div>
      `;

      // Determine icon based on results
      const icon =
        errorRecords.length === 0
          ? "success"
          : successRecords.length === 0
            ? "error"
            : "info";
      const title =
        errorRecords.length === 0
          ? "All Students Added!"
          : successRecords.length === 0
            ? "Addition Failed"
            : "Student Addition Summary";

      Swal.fire({
        icon: icon,
        title: title,
        html: tableHtml,
        width: "90%",
        timer: null,
        timerProgressBar: false,
        showConfirmButton: true,
        confirmButtonText: "Got it!",
      });

      if (successRecords.length > 0) {
        onStudentsCreated?.();
        handleClose();
      }
    }
  };

  // Handle Excel file upload
  const handleExcelUpload = async (event) => {
    if (isParsing) return;
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsParsing(true);
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });
      const worksheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[worksheetName];

      if (!worksheet) {
        swalError("No worksheet found in the Excel file.");
        return;
      }

      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (jsonData.length === 0) {
        swalWarning("No valid data found in the Excel file.");
        return;
      }

      const headerRow = jsonData[0];
      const dataRows = jsonData.slice(1);
      const headerMap = {};
      const newRows = [];

      // Map Excel column headers to our fields
      headerRow.forEach((cell, colNumber) => {
        const headerValue = String(cell || "")
          .toLowerCase()
          .trim();
        if (headerValue.includes("first")) headerMap[colNumber] = "firstName";
        else if (
          headerValue.includes("last") ||
          headerValue.includes("surname")
        )
          headerMap[colNumber] = "lastName";
        else if (
          headerValue.includes("roll")
        )
          headerMap[colNumber] = "rollNo";
        else if (
          headerValue.includes("reference") ||
          (headerValue.includes("id") && !headerValue.includes("email")) ||
          headerValue.includes("admission")
        )
          headerMap[colNumber] = "referenceId";
        else if (
          headerValue === "email" ||
          (headerValue.includes("email") &&
            !headerValue.includes("message") &&
            !headerValue.includes("send"))
        )
          headerMap[colNumber] = "email";
        else if (headerValue.includes("phone"))
          headerMap[colNumber] = "phoneNumber";
        else if (
          headerValue.includes("emailmessage") ||
          headerValue === "emailmessages" ||
          headerValue.includes("send email")
        )
          headerMap[colNumber] = "receiveEmailMessages";
        else if (
          headerValue.includes("whatsapp") ||
          headerValue.includes("send whatsapp")
        )
          headerMap[colNumber] = "receiveWhatsAppMessages";
      });

      // Parse data rows
      dataRows.forEach((row) => {
        const newRow = createEmptyRow();

        Object.keys(headerMap).forEach((colNumber) => {
          const field = headerMap[colNumber];
          const cellValue = row[colNumber];

          if (field) {
            newRow[field] = String(cellValue || "").trim();

            // Convert YES/NO strings to boolean for toggle fields
            if (
              field === "receiveEmailMessages" ||
              field === "receiveWhatsAppMessages"
            ) {
              const val = String(cellValue || "")
                .toLowerCase()
                .trim();
              newRow[field] = val === "yes" || val === "true" || val === "1";
            }
          }
        });

        // Only add row if it has at least firstName or referenceId
        if (newRow.firstName || newRow.referenceId) {
          newRows.push(newRow);
        }
      });

      if (newRows.length === 0) {
        swalWarning("No valid data found in the Excel file.");
        return;
      }

      setRows(newRows);
      swalSuccess(
        `Loaded ${newRows.length} student${newRows.length > 1 ? "s" : ""} from Excel.`,
      );
    } catch (err) {
      console.error("Excel parse error:", err);
      swalError("Failed to parse Excel file. Please check the format.");
    } finally {
      setIsParsing(false);
    }

    // Reset file input
    event.target.value = "";
  };

  // Close modal and reset state
  const handleClose = () => {
    if (isCreating) return;
    setRows([createEmptyRow()]);
    setSelectedLevel(null);
    setSelectedInstructorId(null);
    setSelectedBatch(null);
    setCommonPassword("");
    setShowCommonPassword(false);
    onClose();
  };

  // Download enrollment template
  const downloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "/files/student_list_template.xlsx";
    link.download = "student_list_template.xlsx";
    link.click();
  };

  // Custom cell renderer
  const renderCell = useCallback(
    (header, row, rowIndex) => {
      const value = row[header.key];
      const error = row.errors?.[header.key];
      const hasError = !!error;

      // Toggle fields
      if (
        ["locked", "receiveWhatsAppMessages", "receiveEmailMessages"].includes(
          header.key,
        )
      ) {
        const isDisabled =
          isCreating ||
          (header.key === "receiveWhatsAppMessages" && !row.phoneNumber) ||
          (header.key === "receiveEmailMessages" && !row.email);

        return (
          <label className="table-cell-toggle">
            <input
              type="checkbox"
              checked={value || false}
              onChange={(e) =>
                handleRowChange(rowIndex, header.key, e.target.checked)
              }
              disabled={isDisabled}
            />
            <span className="toggle-slider"></span>
          </label>
        );
      }

      // Phone number field with PhoneInput
      if (header.key === "phoneNumber") {
        return (
          <div
            className={`table-input-wrapper table-phone-wrapper ${hasError ? "has-error" : ""}`}
          >
            <PhoneInput
              country={userCountry}
              value={value || ""}
              onChange={(val) => handleRowChange(rowIndex, "phoneNumber", val)}
              disabled={isCreating}
              inputStyle={{
                width: "100%",
                height: "36px",
                fontSize: "14px",
                paddingLeft: "42px",
                border: hasError ? "2px solid #ef4444" : "2px solid #e2e8f0",
                borderRadius: "6px",
              }}
              buttonStyle={{
                height: "36px",
                border: hasError ? "2px solid #ef4444" : "2px solid #e2e8f0",
                borderRadius: "6px 0 0 6px",
              }}
              containerStyle={{
                width: "100%",
              }}
              dropdownStyle={{
                textAlign: "left",
                width: "inherit",
              }}
              enableSearch={true}
              disableSearchIcon={true}
              placeholder="Phone number"
            />
            {hasError && (
              <span className="table-validation-error">{error}</span>
            )}
          </div>
        );
      }

      // Actions column
      if (header.key === "actions") {
        return (
          <button
            type="button"
            className="modal-table-remove-btn"
            onClick={() => handleRowRemove(rowIndex)}
            disabled={isCreating || rows.length <= 1}
            title="Remove row"
          >
            <i className="fa-solid fa-trash"></i>
          </button>
        );
      }

      // Text/Email inputs
      return (
        <div className={`table-input-wrapper ${hasError ? "has-error" : ""}`}>
          <input
            type={header.key === "email" ? "email" : "text"}
            className={`modal-table-input ${hasError ? "input-error" : ""}`}
            value={value || ""}
            onChange={(e) =>
              handleRowChange(rowIndex, header.key, e.target.value)
            }
            placeholder={header.placeholder || ""}
            disabled={isCreating}
          />
          {hasError && <span className="table-validation-error">{error}</span>}
        </div>
      );
    },
    [handleRowChange, handleRowRemove, isCreating, rows.length],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} maxWidth="95%">
      {isCreating ? (
        <div className="bulk-progress-overlay">
          <div className="progress-content">
            <Loader style={{ fontSize: "3rem", left: "0" }} />
            <h3>Adding Students...</h3>
            <p className="progress-text">
              Adding <strong>{currentProgress.name}</strong>
            </p>
            <p className="progress-count">
              {currentProgress.current} of {currentProgress.total}
            </p>
            <div className="progress-bar-container">
              <div
                className="progress-bar-fill"
                style={{
                  width: `${(currentProgress.current / currentProgress.total) * 100}%`,
                }}
              />
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Modal Header */}
          <div className="add-students-header">
            <div className="header-left">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  flexWrap: "wrap",
                }}
              >
                <h2 style={{ margin: 0 }}>Add Students</h2>
                {instituteCategory && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <span style={{ color: "#6b7280", fontSize: "1.1rem" }}>
                      in
                    </span>
                    {instituteCategory !== "HIGHER_EDUCATION" && (
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                        <Select
                          value={
                            modalLevelOptions.find(
                              (opt) => opt.value === selectedLevel,
                            ) || null
                          }
                          onChange={(option) => {
                            setSelectedLevel(option?.value || null);
                            if (option?.value !== "LEVEL_TEST_REQUIRED") {
                              setSelectedInstructorId(null);
                            }
                          }}
                          options={modalLevelOptions}
                          placeholder="Select Level..."
                          isClearable
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: "36px",
                              minWidth: "190px",
                              borderColor: !selectedLevel ? "#fcd34d" : "#e2e8f0",
                              backgroundColor: !selectedLevel
                                ? "#fffbeb"
                                : "white",
                              boxShadow: "none",
                            }),
                            valueContainer: (base) => ({
                              ...base,
                              padding: "0 8px",
                            }),
                            input: (base) => ({
                              ...base,
                              margin: 0,
                              padding: 0,
                            }),
                          }}
                        />

                        {selectedLevel === "LEVEL_TEST_REQUIRED" && (
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <span style={{ color: "#475569", fontSize: "0.88rem", fontWeight: "600" }}>
                              &bull; Assign to Teacher:
                            </span>
                            <Select
                              value={
                                instructorOptions.find(
                                  (opt) => opt.value === selectedInstructorId,
                                ) || null
                              }
                              onChange={(option) =>
                                setSelectedInstructorId(option?.value || null)
                              }
                              options={instructorOptions}
                              placeholder="Select Teacher / Evaluator..."
                              isClearable
                              styles={{
                                control: (base) => ({
                                  ...base,
                                  minHeight: "36px",
                                  minWidth: "260px",
                                  borderColor: !selectedInstructorId ? "#f59e0b" : "#10b981",
                                  backgroundColor: !selectedInstructorId
                                    ? "#fffbeb"
                                    : "#f0fdf4",
                                  boxShadow: "none",
                                }),
                                valueContainer: (base) => ({
                                  ...base,
                                  padding: "0 8px",
                                }),
                                input: (base) => ({
                                  ...base,
                                  margin: 0,
                                  padding: 0,
                                }),
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                    {instituteCategory === "HIGHER_EDUCATION" && (
                      <Select
                        value={
                          batchYearOptions.find(
                            (opt) => opt.value === selectedBatch,
                          ) || null
                        }
                        onChange={(option) =>
                          setSelectedBatch(option?.value || null)
                        }
                        options={batchYearOptions}
                        placeholder="Select Batch..."
                        isClearable
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: "36px",
                            minWidth: "140px",
                            borderColor: !selectedBatch ? "#fcd34d" : "#e2e8f0",
                            backgroundColor: !selectedBatch
                              ? "#fffbeb"
                              : "white",
                            boxShadow: "none",
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            padding: "0 8px",
                          }),
                          input: (base) => ({
                            ...base,
                            margin: 0,
                            padding: 0,
                          }),
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
              <p className="modal-description" style={{ marginBottom: "0.75rem" }}>Enter student details below.</p>
              {/* Common Password Input */}
              {!autoGenerateStudentPassword && (
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    padding: "0.5rem 0.85rem",
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.02)",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.4rem",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      color: "#475569",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <i className="fa-solid fa-lock" style={{ color: "#64748b", fontSize: "0.8rem" }}></i>
                    Initial Password:
                  </label>
                  <div style={{ position: "relative", width: "240px" }}>
                    <input
                      type={showCommonPassword ? "text" : "password"}
                      value={commonPassword}
                      onChange={(e) => setCommonPassword(e.target.value)}
                      placeholder="Leave empty if not needed"
                      autoComplete="new-password"
                      style={{
                        height: "32px",
                        fontSize: "0.85rem",
                        padding: "0 32px 0 10px",
                        border: commonPassword && commonPassword.length < 8 ? "1px solid #ef4444" : "1px solid #cbd5e1",
                        borderRadius: "6px",
                        width: "100%",
                        backgroundColor: "#ffffff",
                        outline: "none",
                        boxSizing: "border-box",
                        transition: "all 0.2s ease",
                      }}
                      onFocus={(e) => {
                        if (!commonPassword || commonPassword.length >= 8) {
                          e.target.style.borderColor = "#94a3b8";
                          e.target.style.boxShadow = "0 0 0 1px #94a3b8";
                        }
                      }}
                      onBlur={(e) => {
                        e.target.style.boxShadow = "none";
                        e.target.style.borderColor = commonPassword && commonPassword.length < 8 ? "#ef4444" : "#cbd5e1";
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowCommonPassword((prev) => !prev)}
                      style={{
                        position: "absolute",
                        right: "8px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#94a3b8",
                        padding: "4px",
                        fontSize: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "color 0.2s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = "#475569")}
                      onMouseLeave={(e) => (e.currentTarget.style.color = "#94a3b8")}
                      title={showCommonPassword ? "Hide password" : "Show password"}
                      tabIndex={-1}
                    >
                      <i className={`fa-solid ${showCommonPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                    </button>
                  </div>
                  {commonPassword && commonPassword.length < 8 && (
                    <small style={{ color: "#ef4444", fontSize: "0.75rem", fontWeight: 500 }}>
                      Min. 8 chars
                    </small>
                  )}
                </div>
              )}
            </div>
            <div className="header-right">
              <div className="excel-upload-wrapper">
                <label
                  className={`excel-upload-btn ${isParsing ? "disabled" : ""}`}
                >
                  {isParsing ? (
                    <i className="fa-solid fa-spinner fa-spin"></i>
                  ) : (
                    <FontAwesomeIcon icon={faFileExcel} />
                  )}
                  <span>
                    {isParsing
                      ? "Reading excel file..."
                      : "Autofill from Excel"}
                  </span>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleExcelUpload}
                    style={{ display: "none" }}
                    disabled={isParsing}
                  />
                </label>
                <button
                  type="button"
                  className="template-download-link"
                  onClick={downloadTemplate}
                  title="Download enrollment template Excel file"
                >
                  <FontAwesomeIcon icon={faDownload} />
                  <span>Don't have a file? Download template</span>
                </button>
              </div>
            </div>
          </div>

          {/* Table Body */}
          <ModalTableInput
            headers={headers}
            rows={rows}
            onRowChange={handleRowChange}
            onRowRemove={handleRowRemove}
            onRowAdd={handleRowAdd}
            renderCell={renderCell}
            addButtonText="Add Another Student"
            footerContent={
              <span className="row-count">
                {filledRowsCount} student{filledRowsCount !== 1 ? "s" : ""} to add
              </span>
            }
          />

          {/* Modal Footer */}
          <div className="add-students-footer">
            <button
              type="button"
              className="create-btn"
              onClick={handleSave}
              disabled={!hasAnyData || isCreating}
            >
              <FontAwesomeIcon icon={faSave} />
              Save {filledRowsCount} Student{filledRowsCount !== 1 ? "s" : ""}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default AddStudentsModal;
